// ═══════════════════════════════════════════════════════════════════════
// Part 7+10: Anomaly / Exception Review Panel
// Part 10: Added bulk actions (select all, bulk review/ignore/re-flag),
// better empty states, improved error handling, collapsible panel
// Fixed: PriceUpdateAction now shows for ALL fare_variance flags with
// a related row, not just "Same route" messages. Import moved to top.
// ═══════════════════════════════════════════════════════════════════════

import React, { useState, useMemo, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip';
import { toast } from '@/components/ui/use-toast';
import { supabase } from '@/lib/supabase';
import {
  AlertTriangle, Shield, ShieldCheck, ShieldX, Eye, EyeOff,
  ChevronDown, ChevronRight, MessageSquare, Search, X,
  ArrowRight, Loader2, SlidersHorizontal, BarChart3,
  CheckSquare, Square, ListChecks, DollarSign, ArrowLeftRight,
  Pencil, Check, RefreshCw, Maximize2, Minimize2,
} from 'lucide-react';

import type {
  ExportJobRow,
  AnomalyConfig,
  AnomalyType,
  AnomalyFlag,
  RowAnomalyResult,
  AnomalySummary,
  ReturnTripWindow,
} from './types';
import {
  ANOMALY_TYPE_INFO,
  RETURN_TRIP_WINDOW_INFO,
  DEFAULT_ANOMALY_CONFIG,
} from './types';
import { runAnomalyDetection, mergeReviewStatuses } from './anomalyEngine';

// ─── Props ──────────────────────────────────────────────────────────

interface AnomalyPanelProps {
  rows: ExportJobRow[];
  anomalyResults: Map<string, RowAnomalyResult> | null;
  anomalySummary: AnomalySummary | null;
  anomalyConfig: AnomalyConfig;
  onConfigChange: (config: AnomalyConfig) => void;
  onRunDetection: () => void;
  onResultsChange: (results: Map<string, RowAnomalyResult>, summary: AnomalySummary) => void;
  isRunning: boolean;
}

// ─── Price Update Action Sub-Component ──────────────────────────────
// Shows for ANY fare_variance flag with a relatedRowId.
// Displays both prices side-by-side, with quick-match buttons and
// a custom price input. Saves back to taxicaller_data.grand_total.

const PriceUpdateAction: React.FC<{
  flag: AnomalyFlag;
  currentRow: ExportJobRow;
  relatedRowId: string;
  allRows: ExportJobRow[];
  onPriceUpdated?: () => void;
}> = ({ flag, currentRow, relatedRowId, allRows, onPriceUpdated }) => {
  const [customPrice, setCustomPrice] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  // Find the related row to show its details
  const relatedRow = useMemo(
    () => allRows.find(r => r.tc_id === relatedRowId),
    [allRows, relatedRowId]
  );

  const thisPrice = Number(currentRow.grand_total || 0);
  const relatedPrice = relatedRow ? Number(relatedRow.grand_total || 0) : null;
  const relatedJobId = relatedRow?.job_id || relatedRowId.slice(0, 8) + '...';

  const doUpdate = async (tcId: string, newPrice: number, label: string) => {
    if (!window.confirm(`Update grand_total to $${newPrice.toFixed(2)} for ${label}?\n\nThis will save directly to the source data.`)) return;

    setSaving(true);
    setSavedMessage(null);
    try {
      const { error } = await supabase
        .from('taxicaller_data')
        .update({ grand_total: newPrice })
        .eq('id', tcId);

      if (error) throw error;

      setSavedMessage(`Saved $${newPrice.toFixed(2)} to ${label}`);
      setCustomPrice('');
      toast({
        title: 'Price Updated',
        description: `Grand total for ${label} updated to $${newPrice.toFixed(2)}. Click Re-scan to refresh anomalies.`,
      });
      onPriceUpdated?.();
    } catch (err: any) {
      toast({ title: 'Update Failed', description: err.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleCustomSave = (target: 'this' | 'related') => {
    const price = parseFloat(customPrice);
    if (isNaN(price) || price < 0) {
      toast({ title: 'Invalid price', description: 'Please enter a valid positive number.', variant: 'destructive' });
      return;
    }
    const tcId = target === 'this' ? currentRow.tc_id : relatedRowId;
    const label = target === 'this' ? `Job ${currentRow.job_id}` : `Job ${relatedJobId}`;
    doUpdate(tcId, price, label);
  };

  return (
    <div className="rounded-md border border-blue-600/50 bg-blue-950/30 overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-blue-900/20 border-b border-blue-700/30">
        <DollarSign className="w-3.5 h-3.5 text-blue-400" />
        <span className="text-[10px] font-semibold text-blue-400">Price Discrepancy — Update Price</span>
        {saving && <Loader2 className="w-3 h-3 animate-spin text-blue-400 ml-auto" />}
      </div>

      <div className="px-2.5 py-2 space-y-2">
        {/* Side-by-side price comparison */}
        <div className="grid grid-cols-2 gap-2">
          {/* This Job */}
          <div className="rounded border border-border/50 bg-black/20 p-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-muted-foreground uppercase font-medium">This Job</span>
              <Badge className="text-[8px] px-1 py-0 bg-blue-900/30 text-blue-400 border-blue-700/40">
                {currentRow.job_id}
              </Badge>
            </div>
            <div className="text-sm font-mono font-bold text-foreground">${thisPrice.toFixed(2)}</div>
            <div className="text-[9px] text-muted-foreground truncate" title={currentRow.pick_up_location || ''}>
              {currentRow.pick_up_location || '?'}
            </div>
            <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
              <ArrowRight className="w-2 h-2 flex-shrink-0" />
              <span className="truncate" title={currentRow.drop_off_location || ''}>{currentRow.drop_off_location || '?'}</span>
            </div>
          </div>

          {/* Related Job */}
          <div className="rounded border border-border/50 bg-black/20 p-2 space-y-1">
            <div className="flex items-center justify-between">
              <span className="text-[9px] text-muted-foreground uppercase font-medium">Related Job</span>
              <Badge className="text-[8px] px-1 py-0 bg-amber-900/30 text-amber-400 border-amber-700/40">
                {relatedJobId}
              </Badge>
            </div>
            <div className="text-sm font-mono font-bold text-foreground">
              {relatedPrice !== null ? `$${relatedPrice.toFixed(2)}` : '—'}
            </div>
            {relatedRow ? (
              <>
                <div className="text-[9px] text-muted-foreground truncate" title={relatedRow.pick_up_location || ''}>
                  {relatedRow.pick_up_location || '?'}
                </div>
                <div className="flex items-center gap-1 text-[9px] text-muted-foreground">
                  <ArrowRight className="w-2 h-2 flex-shrink-0" />
                  <span className="truncate" title={relatedRow.drop_off_location || ''}>{relatedRow.drop_off_location || '?'}</span>
                </div>
              </>
            ) : (
              <div className="text-[9px] text-muted-foreground/50 italic">Related row not in current dataset</div>
            )}
          </div>
        </div>

        {/* Difference callout */}
        {relatedPrice !== null && (
          <div className="flex items-center justify-center gap-2 py-1">
            <ArrowLeftRight className="w-3 h-3 text-amber-400" />
            <span className="text-[10px] font-medium text-amber-400">
              Difference: ${Math.abs(thisPrice - relatedPrice).toFixed(2)}
            </span>
          </div>
        )}

        {/* Quick Match Buttons */}
        <div className="space-y-1.5">
          <div className="text-[9px] text-muted-foreground uppercase font-medium">Quick Actions</div>
          <div className="flex flex-wrap gap-1.5">
            {/* Match this job's price to the related job's price */}
            {relatedPrice !== null && relatedPrice !== thisPrice && (
              <Button
                size="sm"
                onClick={() => doUpdate(currentRow.tc_id, relatedPrice, `Job ${currentRow.job_id}`)}
                disabled={saving}
                className="h-6 text-[9px] px-2 bg-blue-900/40 text-blue-400 border border-blue-700/40 hover:bg-blue-900/60"
              >
                <Pencil className="w-2.5 h-2.5 mr-1" />
                Set this to ${relatedPrice.toFixed(2)}
              </Button>
            )}

            {/* Match related job's price to this job's price */}
            {relatedPrice !== null && relatedPrice !== thisPrice && (
              <Button
                size="sm"
                onClick={() => doUpdate(relatedRowId, thisPrice, `Job ${relatedJobId}`)}
                disabled={saving}
                className="h-6 text-[9px] px-2 bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-900/60"
              >
                <Pencil className="w-2.5 h-2.5 mr-1" />
                Set related to ${thisPrice.toFixed(2)}
              </Button>
            )}
          </div>
        </div>

        {/* Custom Price Input */}
        <div className="space-y-1.5">
          <div className="text-[9px] text-muted-foreground uppercase font-medium">Or enter a custom price</div>
          <div className="flex items-center gap-1.5">
            <span className="text-[10px] text-muted-foreground">$</span>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={customPrice}
              onChange={e => setCustomPrice(e.target.value)}
              placeholder="Enter new price..."
              className="h-6 flex-1 text-[10px] px-2 bg-black/40 border-border"
              onKeyDown={e => {
                if (e.key === 'Enter' && customPrice) handleCustomSave('this');
              }}
            />
            <Button
              size="sm"
              onClick={() => handleCustomSave('this')}
              disabled={saving || !customPrice}
              className="h-6 text-[9px] px-2 bg-blue-900/40 text-blue-400 border border-blue-700/40 hover:bg-blue-900/60"
              title={`Save to Job ${currentRow.job_id}`}
            >
              {saving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5 mr-0.5" />}
              This Job
            </Button>
            {relatedRow && (
              <Button
                size="sm"
                onClick={() => handleCustomSave('related')}
                disabled={saving || !customPrice}
                className="h-6 text-[9px] px-2 bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-900/60"
                title={`Save to Job ${relatedJobId}`}
              >
                {saving ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : <Check className="w-2.5 h-2.5 mr-0.5" />}
                Related
              </Button>
            )}
          </div>
        </div>

        {/* Success message */}
        {savedMessage && (
          <div className="flex items-center gap-1.5 text-[9px] text-green-400 bg-green-900/20 border border-green-700/30 rounded px-2 py-1">
            <Check className="w-3 h-3 flex-shrink-0" />
            <span>{savedMessage}</span>
            <span className="text-muted-foreground ml-1">— Click Re-scan to refresh</span>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Main Component ─────────────────────────────────────────────────

const AnomalyPanel: React.FC<AnomalyPanelProps> = ({
  rows, anomalyResults, anomalySummary, anomalyConfig,
  onConfigChange, onRunDetection, onResultsChange, isRunning,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [poppedOut, setPoppedOut] = useState(false);
  const [configExpanded, setConfigExpanded] = useState(false);
  const [filterType, setFilterType] = useState<AnomalyType | 'all'>('all');
  const [filterStatus, setFilterStatus] = useState<'all' | 'unreviewed' | 'reviewed' | 'ignored'>('all');
  const [searchText, setSearchText] = useState('');
  const [expandedRowId, setExpandedRowId] = useState<string | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [saving, setSaving] = useState(false);

  // Part 10: Bulk selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkNote, setBulkNote] = useState('');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [showBulkActions, setShowBulkActions] = useState(false);

  // ─── Filtered Results ───────────────────────────────────────────
  const filteredResults = useMemo(() => {
    if (!anomalyResults) return [];
    const arr = Array.from(anomalyResults.values());

    return arr.filter(result => {
      if (filterType !== 'all') {
        if (!result.flags.some(f => f.type === filterType)) return false;
      }
      if (filterStatus !== 'all') {
        if (filterStatus === 'unreviewed' && result.reviewStatus !== 'flagged') return false;
        if (filterStatus === 'reviewed' && result.reviewStatus !== 'reviewed') return false;
        if (filterStatus === 'ignored' && result.reviewStatus !== 'ignored') return false;
      }
      if (searchText.trim()) {
        const s = searchText.toLowerCase();
        const row = rows.find(r => r.tc_id === result.rowId);
        if (!row) return false;
        const searchable = [
          result.jobId, row.account_id, row.account_name, row.passenger_name,
          row.pick_up_location, row.drop_off_location,
          ...result.flags.map(f => f.message),
        ].filter(Boolean).join(' ').toLowerCase();
        if (!searchable.includes(s)) return false;
      }
      return true;
    });
  }, [anomalyResults, filterType, filterStatus, searchText, rows]);

  // ─── Config Handlers ────────────────────────────────────────────
  const toggleType = useCallback((type: AnomalyType) => {
    const newTypes = new Set(anomalyConfig.enabledTypes);
    if (newTypes.has(type)) newTypes.delete(type);
    else newTypes.add(type);
    onConfigChange({ ...anomalyConfig, enabledTypes: newTypes });
  }, [anomalyConfig, onConfigChange]);

  // ─── Selection Handlers (Part 10) ───────────────────────────────
  const toggleSelection = useCallback((rowId: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  }, []);

  const selectAllFiltered = useCallback(() => {
    const allIds = new Set(filteredResults.map(r => r.rowId));
    setSelectedIds(allIds);
  }, [filteredResults]);

  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
  }, []);

  const isAllSelected = filteredResults.length > 0 && filteredResults.every(r => selectedIds.has(r.rowId));
  const selectedCount = selectedIds.size;

  // ─── Recompute Summary Helper ──────────────────────────────────
  const recomputeSummary = useCallback((updated: Map<string, RowAnomalyResult>) => {
    const allTypes: AnomalyType[] = ['fare_variance', 'missing_return', 'missing_fields', 'validation_issue', 'tm_mismatch', 'duplicate_trip', 'outlier_pricing'];
    const byType: Record<AnomalyType, number> = {} as any;
    for (const t of allTypes) byType[t] = 0;
    let totalFlags = 0, reviewed = 0, ignored = 0, unreviewed = 0;
    for (const r of updated.values()) {
      for (const f of r.flags) { byType[f.type]++; totalFlags++; }
      if (r.reviewStatus === 'reviewed') reviewed++;
      else if (r.reviewStatus === 'ignored') ignored++;
      else unreviewed++;
    }
    return { totalFlagged: updated.size, totalFlags, byType, reviewed, ignored, unreviewed };
  }, []);

  // ─── Single Review Handler ──────────────────────────────────────
  const handleReview = useCallback(async (
    result: RowAnomalyResult,
    status: 'reviewed' | 'ignored' | 'flagged'
  ) => {
    setSaving(true);
    try {
      if (status === 'flagged') {
        // Delete the review record to re-flag
        const { error } = await supabase.from('export_anomaly_reviews')
          .delete()
          .eq('job_id', result.jobId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('export_anomaly_reviews').insert({
          job_id: result.jobId,
          anomaly_type: result.flags.map(f => f.type).join(','),
          status,
          notes: reviewNote.trim() || null,
          reviewed_by: 'admin',
          reviewed_at: new Date().toISOString(),
        });
        if (error) throw error;
      }

      if (anomalyResults) {
        const updated = new Map(anomalyResults);
        const existing = updated.get(result.rowId);
        if (existing) {
          updated.set(result.rowId, {
            ...existing,
            reviewStatus: status,
            reviewNotes: status === 'flagged' ? null : (reviewNote.trim() || null),
            reviewedBy: status === 'flagged' ? null : 'admin',
            reviewedAt: status === 'flagged' ? null : new Date().toISOString(),
          });
          onResultsChange(updated, recomputeSummary(updated));
        }
      }

      setReviewNote('');
      toast({
        title: status === 'reviewed' ? 'Marked as Reviewed' : status === 'ignored' ? 'Marked as Ignored' : 'Re-flagged',
        description: `Job ${result.jobId} ${status}`,
      });
    } catch (err: any) {
      toast({ title: 'Review Save Failed', description: err.message || 'Failed to save review. The change was not persisted.', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  }, [anomalyResults, reviewNote, onResultsChange, recomputeSummary]);

  // ─── Bulk Review Handler (Part 10) ──────────────────────────────
  const handleBulkAction = useCallback(async (status: 'reviewed' | 'ignored' | 'flagged') => {
    if (selectedIds.size === 0 || !anomalyResults) return;
    setBulkSaving(true);

    const selectedResults = filteredResults.filter(r => selectedIds.has(r.rowId));
    let successCount = 0;
    let failCount = 0;

    try {
      for (const result of selectedResults) {
        try {
          if (status === 'flagged') {
            const { error } = await supabase.from('export_anomaly_reviews')
              .delete()
              .eq('job_id', result.jobId);
            if (error) throw error;
          } else {
            const { error } = await supabase.from('export_anomaly_reviews').insert({
              job_id: result.jobId,
              anomaly_type: result.flags.map(f => f.type).join(','),
              status,
              notes: bulkNote.trim() || null,
              reviewed_by: 'admin',
              reviewed_at: new Date().toISOString(),
            });
            if (error) throw error;
          }
          successCount++;
        } catch {
          failCount++;
        }
      }

      // Update local state for all successful ones
      const updated = new Map(anomalyResults);
      for (const result of selectedResults) {
        const existing = updated.get(result.rowId);
        if (existing) {
          updated.set(result.rowId, {
            ...existing,
            reviewStatus: status,
            reviewNotes: status === 'flagged' ? null : (bulkNote.trim() || null),
            reviewedBy: status === 'flagged' ? null : 'admin',
            reviewedAt: status === 'flagged' ? null : new Date().toISOString(),
          });
        }
      }
      onResultsChange(updated, recomputeSummary(updated));
      setSelectedIds(new Set());
      setBulkNote('');

      const statusLabel = status === 'reviewed' ? 'reviewed' : status === 'ignored' ? 'ignored' : 're-flagged';
      toast({
        title: `Bulk Action Complete`,
        description: `${successCount} row(s) marked as ${statusLabel}${failCount > 0 ? `. ${failCount} failed.` : ''}`,
        variant: failCount > 0 ? 'destructive' : 'default',
      });
    } catch (err: any) {
      toast({ title: 'Bulk Action Failed', description: err.message || 'An unexpected error occurred during the bulk action.', variant: 'destructive' });
    } finally {
      setBulkSaving(false);
    }
  }, [selectedIds, filteredResults, anomalyResults, bulkNote, onResultsChange, recomputeSummary]);

  // ─── Get Row Data ───────────────────────────────────────────────
  const getRow = useCallback((rowId: string) => rows.find(r => r.tc_id === rowId), [rows]);

  const allTypes: AnomalyType[] = ['fare_variance', 'missing_return', 'missing_fields', 'validation_issue', 'tm_mismatch', 'duplicate_trip', 'outlier_pricing'];

  return (
    <TooltipProvider delayDuration={200}>
      <Card className="bg-card border-border">
        <CardHeader
          className="pb-2 pt-3 px-4 cursor-pointer select-none hover:bg-secondary/20 transition-colors"
          onClick={(e) => {
            // Don't collapse when clicking buttons inside the header
            if ((e.target as HTMLElement).closest('button')) return;
            setCollapsed(!collapsed);
          }}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {collapsed ? (
                <ChevronRight className="w-4 h-4 text-muted-foreground transition-transform" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform" />
              )}
              <AlertTriangle className="w-4 h-4 text-amber-400" />
              <CardTitle className="text-sm font-semibold">Exceptions / Anomalies</CardTitle>
              {anomalySummary && (
                <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[10px] px-1.5 py-0">
                  {anomalySummary.totalFlagged} flagged
                </Badge>
              )}
              {collapsed && anomalySummary && (
                <span className="text-[10px] text-muted-foreground/60 ml-1">
                  {anomalySummary.reviewed} reviewed · {anomalySummary.ignored} ignored · {anomalySummary.unreviewed} pending
                </span>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              {!collapsed && (
                <Button
                  size="sm" variant="ghost"
                  onClick={(e) => { e.stopPropagation(); setConfigExpanded(!configExpanded); }}
                  className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                >
                  <SlidersHorizontal className="w-3 h-3 mr-1" />
                  Config
                </Button>
              )}
              {!collapsed && anomalyResults && anomalyResults.size > 0 && (
                <Button
                  size="sm" variant="ghost"
                  onClick={(e) => { e.stopPropagation(); setPoppedOut(true); }}
                  className="h-6 text-[10px] px-2 text-muted-foreground hover:text-foreground"
                  title="Pop out to full-screen view"
                >
                  <Maximize2 className="w-3 h-3 mr-1" />
                  Pop Out
                </Button>
              )}
              <Button
                size="sm"
                onClick={(e) => { e.stopPropagation(); onRunDetection(); }}
                disabled={isRunning || rows.length === 0}
                className="h-7 text-xs golden-button"
              >
                {isRunning ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Shield className="w-3 h-3 mr-1" />}
                {anomalyResults ? 'Re-scan' : 'Scan'}
              </Button>
            </div>

          </div>
        </CardHeader>

        {!collapsed && (
          <CardContent className="px-4 pb-3 pt-0 space-y-3">
            {/* ─── Config Panel ─── */}
            {configExpanded && (
              <div className="border border-border/60 rounded-md p-3 space-y-3 bg-secondary/10">
                <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
                  <SlidersHorizontal className="w-3.5 h-3.5" />
                  Detection Settings
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase">Fare Variance Threshold</Label>
                    <div className="flex items-center gap-1">
                      <span className="text-xs text-muted-foreground">$</span>
                      <Input
                        type="number" min={0} step={1}
                        value={anomalyConfig.fareVarianceThreshold}
                        onChange={e => onConfigChange({ ...anomalyConfig, fareVarianceThreshold: Number(e.target.value) || 0 })}
                        className="dark-input text-xs h-7 w-20"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase">Return Trip Window</Label>
                    <Select
                      value={anomalyConfig.returnTripWindow}
                      onValueChange={(v: ReturnTripWindow) => onConfigChange({ ...anomalyConfig, returnTripWindow: v })}
                    >
                      <SelectTrigger className="dark-input text-xs h-7"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {Object.entries(RETURN_TRIP_WINDOW_INFO).map(([k, v]) => (
                          <SelectItem key={k} value={k}>{v.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div>
                  <Label className="text-[10px] text-muted-foreground uppercase mb-1 block">Enabled Detectors</Label>
                  <div className="grid grid-cols-2 gap-1">
                    {allTypes.map(type => {
                      const info = ANOMALY_TYPE_INFO[type];
                      return (
                        <label key={type} className="flex items-center gap-1.5 cursor-pointer text-[10px]">
                          <Switch
                            checked={anomalyConfig.enabledTypes.has(type)}
                            onCheckedChange={() => toggleType(type)}
                            className="scale-[0.6]"
                          />
                          <span className={anomalyConfig.enabledTypes.has(type) ? info.color : 'text-muted-foreground/50'}>
                            {info.shortLabel}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* ─── Summary Bar ─── */}
            {anomalySummary && (
              <div className="flex flex-wrap gap-2 text-[10px]">
                {allTypes.map(type => {
                  const count = anomalySummary.byType[type] || 0;
                  if (count === 0) return null;
                  const info = ANOMALY_TYPE_INFO[type];
                  return (
                    <button
                      key={type}
                      onClick={() => setFilterType(filterType === type ? 'all' : type)}
                      className={`px-1.5 py-0.5 rounded border transition-colors ${
                        filterType === type
                          ? info.badgeColor + ' ring-1 ring-current'
                          : 'border-border/40 text-muted-foreground hover:border-border'
                      }`}
                    >
                      {info.shortLabel}: {count}
                    </button>
                  );
                })}
                <div className="h-4 w-px bg-border/40" />
                <span className="text-muted-foreground/60 py-0.5">
                  {anomalySummary.reviewed} reviewed, {anomalySummary.ignored} ignored, {anomalySummary.unreviewed} pending
                </span>
              </div>
            )}

            {/* ─── Filters + Bulk Actions Toggle ─── */}
            {anomalyResults && anomalyResults.size > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      value={searchText}
                      onChange={e => setSearchText(e.target.value)}
                      placeholder="Search flagged rows..."
                      className="dark-input text-xs h-7 pl-7"
                    />
                    {searchText && (
                      <button onClick={() => setSearchText('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                  <Select
                    value={filterStatus}
                    onValueChange={(v: any) => setFilterStatus(v)}
                  >
                    <SelectTrigger className="dark-input text-xs h-7 w-28"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All</SelectItem>
                      <SelectItem value="unreviewed">Unreviewed</SelectItem>
                      <SelectItem value="reviewed">Reviewed</SelectItem>
                      <SelectItem value="ignored">Ignored</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    size="sm"
                    variant={showBulkActions ? 'default' : 'ghost'}
                    onClick={() => { setShowBulkActions(!showBulkActions); if (showBulkActions) clearSelection(); }}
                    className={`h-7 text-[10px] px-2 ${showBulkActions ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-700/40' : 'text-muted-foreground hover:text-foreground'}`}
                  >
                    <ListChecks className="w-3 h-3 mr-1" />
                    Bulk
                  </Button>
                </div>

                {/* ─── Bulk Actions Bar (Part 10) ─── */}
                {showBulkActions && (
                  <div className="border border-indigo-700/30 rounded-md bg-indigo-900/10 p-2.5 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-[10px]">
                        <ListChecks className="w-3.5 h-3.5 text-indigo-400" />
                        <span className="font-medium text-indigo-400">Bulk Actions</span>
                        <Badge className="bg-indigo-900/40 text-indigo-400 border-indigo-700/40 text-[9px] px-1.5 py-0">
                          {selectedCount} selected
                        </Badge>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button
                          size="sm" variant="ghost"
                          onClick={isAllSelected ? clearSelection : selectAllFiltered}
                          className="h-5 text-[9px] px-1.5 text-indigo-400 hover:text-indigo-300"
                        >
                          {isAllSelected ? (
                            <><CheckSquare className="w-3 h-3 mr-0.5" /> Deselect All</>
                          ) : (
                            <><Square className="w-3 h-3 mr-0.5" /> Select All ({filteredResults.length})</>
                          )}
                        </Button>
                      </div>
                    </div>

                    {selectedCount > 0 && (
                      <>
                        <Textarea
                          value={bulkNote}
                          onChange={e => setBulkNote(e.target.value)}
                          placeholder="Add a note to all selected rows (optional)..."
                          className="dark-input text-xs min-h-[32px] resize-none"
                        />
                        <div className="flex gap-1.5">
                          <Button
                            size="sm"
                            onClick={() => handleBulkAction('reviewed')}
                            disabled={bulkSaving}
                            className="h-6 text-[10px] px-2 bg-green-900/40 text-green-400 border border-green-700/40 hover:bg-green-900/60"
                          >
                            {bulkSaving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                            Review All ({selectedCount})
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleBulkAction('ignored')}
                            disabled={bulkSaving}
                            className="h-6 text-[10px] px-2 bg-gray-800/40 text-gray-400 border border-gray-700/40 hover:bg-gray-800/60"
                          >
                            <EyeOff className="w-3 h-3 mr-1" />
                            Ignore All ({selectedCount})
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => handleBulkAction('flagged')}
                            disabled={bulkSaving}
                            className="h-6 text-[10px] px-2 bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-900/60"
                          >
                            <AlertTriangle className="w-3 h-3 mr-1" />
                            Re-flag ({selectedCount})
                          </Button>
                        </div>
                      </>
                    )}

                    {selectedCount === 0 && (
                      <p className="text-[9px] text-muted-foreground/60">
                        Select rows using the checkboxes below, or use "Select All" to select all {filteredResults.length} filtered rows.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ─── No Results State ─── */}
            {!anomalyResults && (
              <div className="text-center py-6 text-muted-foreground">
                <Shield className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">Click "Scan" to run anomaly detection on the current dataset.</p>
                <p className="text-[10px] mt-1 opacity-60">{rows.length} rows will be analyzed.</p>
                {rows.length === 0 && (
                  <p className="text-[10px] mt-2 text-amber-400/60">Load data first using the filters above.</p>
                )}
              </div>
            )}

            {/* ─── Clean State ─── */}
            {anomalyResults && anomalyResults.size === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <ShieldCheck className="w-8 h-8 mx-auto mb-2 text-green-400/40" />
                <p className="text-xs text-green-400/80">No anomalies detected.</p>
                <p className="text-[10px] mt-1 opacity-60">All {rows.length} rows passed checks. The dataset looks clean.</p>
              </div>
            )}

            {/* ─── Filtered Empty State ─── */}
            {anomalyResults && anomalyResults.size > 0 && filteredResults.length === 0 && (
              <div className="text-center py-6 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No anomalies match the current filters.</p>
                <p className="text-[10px] mt-1 opacity-60">
                  Try changing the type filter, status filter, or clearing the search.
                </p>
                <Button
                  size="sm" variant="ghost"
                  onClick={() => { setFilterType('all'); setFilterStatus('all'); setSearchText(''); }}
                  className="mt-2 text-xs text-muted-foreground hover:text-foreground"
                >
                  Clear Filters
                </Button>
              </div>
            )}

            {/* ─── Flagged Rows List ─── */}
            {filteredResults.length > 0 && (
              <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1">
                {filteredResults.map(result => {
                  const row = getRow(result.rowId);
                  const isExpanded = expandedRowId === result.rowId;
                  const isSelected = selectedIds.has(result.rowId);
                  const statusIcon = result.reviewStatus === 'reviewed'
                    ? <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                    : result.reviewStatus === 'ignored'
                      ? <EyeOff className="w-3.5 h-3.5 text-gray-500" />
                      : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;

                  // Check if any flag has a price update option
                  const hasPriceFlags = result.flags.some(f => f.type === 'fare_variance' && f.relatedRowId);

                  return (
                    <div
                      key={result.rowId}
                      className={`border rounded-md transition-colors ${
                        isExpanded ? 'border-amber-700/40 bg-amber-900/5' : isSelected ? 'border-indigo-700/40 bg-indigo-900/5' : 'border-border/50 hover:border-border'
                      } ${result.reviewStatus !== 'flagged' ? 'opacity-60' : ''}`}
                    >
                      {/* Row Header */}
                      <div className="flex items-center gap-2 px-2.5 py-1.5">
                        {/* Bulk selection checkbox */}
                        {showBulkActions && (
                          <Checkbox
                            checked={isSelected}
                            onCheckedChange={() => toggleSelection(result.rowId)}
                            className="flex-shrink-0"
                          />
                        )}
                        <div
                          className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                          onClick={() => setExpandedRowId(isExpanded ? null : result.rowId)}
                        >
                          {statusIcon}
                          <span className="text-xs font-mono text-muted-foreground w-14 flex-shrink-0">{result.jobId}</span>
                          <span className="text-[10px] text-muted-foreground truncate flex-shrink-0 w-14">
                            {row?.account_id || '-'}
                          </span>
                          <span className="text-[10px] text-muted-foreground truncate flex-1 min-w-0">
                            {row?.passenger_name || row?.passenger_first_name || '-'}
                          </span>
                          <div className="flex gap-0.5 flex-shrink-0">
                            {result.flags.map(flag => {
                              const info = ANOMALY_TYPE_INFO[flag.type];
                              return (
                                <Tooltip key={flag.id}>
                                  <TooltipTrigger asChild>
                                    <Badge className={`${info.badgeColor} text-[8px] px-1 py-0`}>
                                      {info.shortLabel}
                                    </Badge>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="bg-card border-border text-xs max-w-[280px]">
                                    <p className="font-medium">{info.label}</p>
                                    <p className="text-muted-foreground text-[10px] mt-0.5">{flag.message}</p>
                                  </TooltipContent>
                                </Tooltip>
                              );
                            })}
                            {/* Show a small $ icon if price update is available */}
                            {hasPriceFlags && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/40 text-[8px] px-1 py-0">
                                    <DollarSign className="w-2.5 h-2.5" />
                                  </Badge>
                                </TooltipTrigger>
                                <TooltipContent side="top" className="bg-card border-border text-xs">
                                  Price can be updated — expand to see options
                                </TooltipContent>
                              </Tooltip>
                            )}
                          </div>
                          {result.reviewStatus !== 'flagged' && (
                            <Badge className={`text-[8px] px-1 py-0 ${
                              result.reviewStatus === 'reviewed'
                                ? 'bg-green-900/30 text-green-400 border-green-700/40'
                                : 'bg-gray-800/40 text-gray-500 border-gray-700/40'
                            }`}>
                              {result.reviewStatus}
                            </Badge>
                          )}
                          {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                        </div>
                      </div>

                      {/* Expanded Detail */}
                      {isExpanded && row && (
                        <div className="px-3 pb-3 pt-1 border-t border-border/30 space-y-2">
                          {/* Trip Info */}
                          <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                            <div>
                              <span className="text-muted-foreground">Account: </span>
                              <span className="text-foreground">{row.account_id} - {row.account_name || '?'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Date: </span>
                              <span className="text-foreground">{row.pu_date} {row.pu_time?.substring(0, 5)}</span>
                            </div>
                            <div className="col-span-2">
                              <span className="text-muted-foreground">Route: </span>
                              <span className="text-foreground">{row.pick_up_location || '?'}</span>
                              <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-muted-foreground" />
                              <span className="text-foreground">{row.drop_off_location || '?'}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Grand Total: </span>
                              <span className="text-foreground font-medium">${Number(row.grand_total || 0).toFixed(2)}</span>
                            </div>
                            <div>
                              <span className="text-muted-foreground">Net After TM: </span>
                              <span className="text-foreground">${Number(row.net_after_tm || 0).toFixed(2)}</span>
                            </div>
                          </div>

                          {/* Anomaly Details + Price Update */}
                          <div className="space-y-2">
                            {result.flags.map(flag => {
                              const info = ANOMALY_TYPE_INFO[flag.type];
                              // Show price update for ANY fare_variance flag that has a relatedRowId
                              const showPriceUpdate = flag.type === 'fare_variance' && !!flag.relatedRowId;

                              return (
                                <div key={flag.id} className="space-y-1.5">
                                  <div className={`flex items-start gap-2 px-2 py-1.5 rounded border ${info.badgeColor}`}>
                                    <AlertTriangle className={`w-3 h-3 flex-shrink-0 mt-0.5 ${info.color}`} />
                                    <div className="flex-1 min-w-0">
                                      <p className={`text-[10px] font-medium ${info.color}`}>{info.label}</p>
                                      <p className="text-[10px] text-foreground/80 mt-0.5">{flag.message}</p>
                                      {flag.detail && (
                                        <p className="text-[9px] text-muted-foreground mt-0.5">{flag.detail}</p>
                                      )}
                                    </div>
                                  </div>
                                  {/* Price Update Action — shown for ALL fare variance flags with a related row */}
                                  {showPriceUpdate && (
                                    <PriceUpdateAction
                                      flag={flag}
                                      currentRow={row}
                                      relatedRowId={flag.relatedRowId!}
                                      allRows={rows}
                                      onPriceUpdated={() => {
                                        // Optionally trigger a re-scan hint
                                      }}
                                    />
                                  )}
                                </div>
                              );
                            })}
                          </div>

                          {/* Review Notes (if already reviewed) */}
                          {result.reviewNotes && (
                            <div className="flex items-start gap-1.5 text-[10px] bg-secondary/20 rounded px-2 py-1.5">
                              <MessageSquare className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                              <div>
                                <span className="text-muted-foreground">Note: </span>
                                <span className="text-foreground">{result.reviewNotes}</span>
                                {result.reviewedBy && (
                                  <span className="text-muted-foreground/60 ml-1">— {result.reviewedBy}</span>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Review Actions */}
                          <div className="space-y-1.5 pt-1 border-t border-border/30">
                            <Textarea
                              value={reviewNote}
                              onChange={e => setReviewNote(e.target.value)}
                              placeholder="Add a review note (optional)..."
                              className="dark-input text-xs min-h-[40px] resize-none"
                            />
                            <div className="flex gap-1.5">
                              <Button
                                size="sm"
                                onClick={() => handleReview(result, 'reviewed')}
                                disabled={saving}
                                className="h-6 text-[10px] px-2 bg-green-900/40 text-green-400 border border-green-700/40 hover:bg-green-900/60"
                              >
                                {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <ShieldCheck className="w-3 h-3 mr-1" />}
                                Mark Reviewed
                              </Button>
                              <Button
                                size="sm"
                                onClick={() => handleReview(result, 'ignored')}
                                disabled={saving}
                                className="h-6 text-[10px] px-2 bg-gray-800/40 text-gray-400 border border-gray-700/40 hover:bg-gray-800/60"
                              >
                                <EyeOff className="w-3 h-3 mr-1" />
                                Ignore
                              </Button>
                              {result.reviewStatus !== 'flagged' && (
                                <Button
                                  size="sm"
                                  onClick={() => handleReview(result, 'flagged')}
                                  disabled={saving}
                                  className="h-6 text-[10px] px-2 bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-900/60"
                                >
                                  <AlertTriangle className="w-3 h-3 mr-1" />
                                  Re-flag
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* ─── Footer Stats ─── */}
            {anomalySummary && anomalySummary.totalFlagged > 0 && (
              <div className="pt-2 border-t border-border/40 flex items-center gap-2 text-[10px] text-muted-foreground">
                <BarChart3 className="w-3 h-3" />
                <span>
                  {filteredResults.length} of {anomalySummary.totalFlagged} shown
                  {filterType !== 'all' && ` (filtered: ${ANOMALY_TYPE_INFO[filterType].label})`}
                  {filterStatus !== 'all' && ` (${filterStatus})`}
                </span>
                {selectedCount > 0 && (
                  <>
                    <div className="h-3 w-px bg-border/40" />
                    <span className="text-indigo-400">{selectedCount} selected</span>
                  </>
                )}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ═══════════════════════════════════════════════════════════════ */}
      {/* ─── Pop-Out Dialog ─── Full-screen anomaly review view        */}
      {/* ═══════════════════════════════════════════════════════════════ */}
      <Dialog open={poppedOut} onOpenChange={setPoppedOut}>
        <DialogContent className="max-w-5xl w-[95vw] max-h-[92vh] flex flex-col p-0 gap-0">
          <DialogHeader className="px-5 py-3 border-b border-border flex-shrink-0">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <DialogTitle className="text-sm font-semibold">Exceptions / Anomalies — Expanded View</DialogTitle>
                {anomalySummary && (
                  <Badge className="bg-amber-900/30 text-amber-400 border-amber-700/40 text-[10px] px-1.5 py-0">
                    {anomalySummary.totalFlagged} flagged
                  </Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                {anomalySummary && (
                  <span className="text-[10px] text-muted-foreground">
                    {anomalySummary.reviewed} reviewed · {anomalySummary.ignored} ignored · {anomalySummary.unreviewed} pending
                  </span>
                )}
                <Button
                  size="sm" variant="ghost"
                  onClick={() => setPoppedOut(false)}
                  className="h-7 text-xs text-muted-foreground hover:text-foreground"
                >
                  <Minimize2 className="w-3.5 h-3.5 mr-1" />
                  Collapse
                </Button>
              </div>
            </div>
            <DialogDescription className="sr-only">
              Full-screen view of flagged anomaly rows for easier review and scrolling.
            </DialogDescription>
          </DialogHeader>

          {/* Summary + Filters */}
          <div className="px-5 py-3 border-b border-border/50 space-y-2 flex-shrink-0">
            {/* Summary Bar */}
            {anomalySummary && (
              <div className="flex flex-wrap gap-2 text-[10px]">
                {allTypes.map(type => {
                  const count = anomalySummary.byType[type] || 0;
                  if (count === 0) return null;
                  const info = ANOMALY_TYPE_INFO[type];
                  return (
                    <button
                      key={type}
                      onClick={() => setFilterType(filterType === type ? 'all' : type)}
                      className={`px-1.5 py-0.5 rounded border transition-colors ${
                        filterType === type
                          ? info.badgeColor + ' ring-1 ring-current'
                          : 'border-border/40 text-muted-foreground hover:border-border'
                      }`}
                    >
                      {info.shortLabel}: {count}
                    </button>
                  );
                })}
              </div>
            )}
            {/* Search + Filter */}
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={searchText}
                  onChange={e => setSearchText(e.target.value)}
                  placeholder="Search flagged rows..."
                  className="dark-input text-xs h-7 pl-7"
                />
                {searchText && (
                  <button onClick={() => setSearchText('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                    <X className="w-3 h-3" />
                  </button>
                )}
              </div>
              <Select value={filterStatus} onValueChange={(v: any) => setFilterStatus(v)}>
                <SelectTrigger className="dark-input text-xs h-7 w-28"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="unreviewed">Unreviewed</SelectItem>
                  <SelectItem value="reviewed">Reviewed</SelectItem>
                  <SelectItem value="ignored">Ignored</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant={showBulkActions ? 'default' : 'ghost'}
                onClick={() => { setShowBulkActions(!showBulkActions); if (showBulkActions) clearSelection(); }}
                className={`h-7 text-[10px] px-2 ${showBulkActions ? 'bg-indigo-900/40 text-indigo-400 border border-indigo-700/40' : 'text-muted-foreground hover:text-foreground'}`}
              >
                <ListChecks className="w-3 h-3 mr-1" />
                Bulk
              </Button>
            </div>

            {/* Bulk Actions in popout */}
            {showBulkActions && (
              <div className="border border-indigo-700/30 rounded-md bg-indigo-900/10 p-2.5 space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-[10px]">
                    <ListChecks className="w-3.5 h-3.5 text-indigo-400" />
                    <span className="font-medium text-indigo-400">Bulk Actions</span>
                    <Badge className="bg-indigo-900/40 text-indigo-400 border-indigo-700/40 text-[9px] px-1.5 py-0">
                      {selectedCount} selected
                    </Badge>
                  </div>
                  <Button
                    size="sm" variant="ghost"
                    onClick={isAllSelected ? clearSelection : selectAllFiltered}
                    className="h-5 text-[9px] px-1.5 text-indigo-400 hover:text-indigo-300"
                  >
                    {isAllSelected ? (
                      <><CheckSquare className="w-3 h-3 mr-0.5" /> Deselect All</>
                    ) : (
                      <><Square className="w-3 h-3 mr-0.5" /> Select All ({filteredResults.length})</>
                    )}
                  </Button>
                </div>
                {selectedCount > 0 && (
                  <div className="flex gap-1.5">
                    <Button size="sm" onClick={() => handleBulkAction('reviewed')} disabled={bulkSaving}
                      className="h-6 text-[10px] px-2 bg-green-900/40 text-green-400 border border-green-700/40 hover:bg-green-900/60">
                      <ShieldCheck className="w-3 h-3 mr-1" /> Review All ({selectedCount})
                    </Button>
                    <Button size="sm" onClick={() => handleBulkAction('ignored')} disabled={bulkSaving}
                      className="h-6 text-[10px] px-2 bg-gray-800/40 text-gray-400 border border-gray-700/40 hover:bg-gray-800/60">
                      <EyeOff className="w-3 h-3 mr-1" /> Ignore All ({selectedCount})
                    </Button>
                    <Button size="sm" onClick={() => handleBulkAction('flagged')} disabled={bulkSaving}
                      className="h-6 text-[10px] px-2 bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-900/60">
                      <AlertTriangle className="w-3 h-3 mr-1" /> Re-flag ({selectedCount})
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Scrollable Rows List — NO max-h constraint, fills remaining dialog space */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1">
            {filteredResults.length === 0 && (
              <div className="text-center py-12 text-muted-foreground">
                <Search className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-xs">No anomalies match the current filters.</p>
              </div>
            )}
            {filteredResults.map(result => {
              const row = getRow(result.rowId);
              const isExpanded = expandedRowId === result.rowId;
              const isSelected = selectedIds.has(result.rowId);
              const statusIcon = result.reviewStatus === 'reviewed'
                ? <ShieldCheck className="w-3.5 h-3.5 text-green-400" />
                : result.reviewStatus === 'ignored'
                  ? <EyeOff className="w-3.5 h-3.5 text-gray-500" />
                  : <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />;
              const hasPriceFlags = result.flags.some(f => f.type === 'fare_variance' && f.relatedRowId);

              return (
                <div
                  key={result.rowId}
                  className={`border rounded-md transition-colors ${
                    isExpanded ? 'border-amber-700/40 bg-amber-900/5' : isSelected ? 'border-indigo-700/40 bg-indigo-900/5' : 'border-border/50 hover:border-border'
                  } ${result.reviewStatus !== 'flagged' ? 'opacity-60' : ''}`}
                >
                  {/* Row Header */}
                  <div className="flex items-center gap-2 px-2.5 py-1.5">
                    {showBulkActions && (
                      <Checkbox checked={isSelected} onCheckedChange={() => toggleSelection(result.rowId)} className="flex-shrink-0" />
                    )}
                    <div
                      className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                      onClick={() => setExpandedRowId(isExpanded ? null : result.rowId)}
                    >
                      {statusIcon}
                      <span className="text-xs font-mono text-muted-foreground w-14 flex-shrink-0">{result.jobId}</span>
                      <span className="text-[10px] text-muted-foreground truncate flex-shrink-0 w-14">{row?.account_id || '-'}</span>
                      <span className="text-[10px] text-muted-foreground truncate flex-1 min-w-0">
                        {row?.passenger_name || row?.passenger_first_name || '-'}
                      </span>
                      <div className="flex gap-0.5 flex-shrink-0">
                        {result.flags.map(flag => {
                          const info = ANOMALY_TYPE_INFO[flag.type];
                          return (
                            <Tooltip key={flag.id}>
                              <TooltipTrigger asChild>
                                <Badge className={`${info.badgeColor} text-[8px] px-1 py-0`}>{info.shortLabel}</Badge>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-card border-border text-xs max-w-[280px]">
                                <p className="font-medium">{info.label}</p>
                                <p className="text-muted-foreground text-[10px] mt-0.5">{flag.message}</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                        {hasPriceFlags && (
                          <Badge className="bg-blue-900/30 text-blue-400 border-blue-700/40 text-[8px] px-1 py-0">
                            <DollarSign className="w-2.5 h-2.5" />
                          </Badge>
                        )}
                      </div>
                      {result.reviewStatus !== 'flagged' && (
                        <Badge className={`text-[8px] px-1 py-0 ${
                          result.reviewStatus === 'reviewed'
                            ? 'bg-green-900/30 text-green-400 border-green-700/40'
                            : 'bg-gray-800/40 text-gray-500 border-gray-700/40'
                        }`}>{result.reviewStatus}</Badge>
                      )}
                      {isExpanded ? <ChevronDown className="w-3 h-3 text-muted-foreground flex-shrink-0" /> : <ChevronRight className="w-3 h-3 text-muted-foreground flex-shrink-0" />}
                    </div>
                  </div>

                  {/* Expanded Detail */}
                  {isExpanded && row && (
                    <div className="px-3 pb-3 pt-1 border-t border-border/30 space-y-2">
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[10px]">
                        <div><span className="text-muted-foreground">Account: </span><span className="text-foreground">{row.account_id} - {row.account_name || '?'}</span></div>
                        <div><span className="text-muted-foreground">Date: </span><span className="text-foreground">{row.pu_date} {row.pu_time?.substring(0, 5)}</span></div>
                        <div className="col-span-2">
                          <span className="text-muted-foreground">Route: </span>
                          <span className="text-foreground">{row.pick_up_location || '?'}</span>
                          <ArrowRight className="w-2.5 h-2.5 inline mx-1 text-muted-foreground" />
                          <span className="text-foreground">{row.drop_off_location || '?'}</span>
                        </div>
                        <div><span className="text-muted-foreground">Grand Total: </span><span className="text-foreground font-medium">${Number(row.grand_total || 0).toFixed(2)}</span></div>
                        <div><span className="text-muted-foreground">Net After TM: </span><span className="text-foreground">${Number(row.net_after_tm || 0).toFixed(2)}</span></div>
                      </div>
                      <div className="space-y-2">
                        {result.flags.map(flag => {
                          const info = ANOMALY_TYPE_INFO[flag.type];
                          const showPriceUpdate = flag.type === 'fare_variance' && !!flag.relatedRowId;
                          return (
                            <div key={flag.id} className="space-y-1.5">
                              <div className={`flex items-start gap-2 px-2 py-1.5 rounded border ${info.badgeColor}`}>
                                <AlertTriangle className={`w-3 h-3 flex-shrink-0 mt-0.5 ${info.color}`} />
                                <div className="flex-1 min-w-0">
                                  <p className={`text-[10px] font-medium ${info.color}`}>{info.label}</p>
                                  <p className="text-[10px] text-foreground/80 mt-0.5">{flag.message}</p>
                                  {flag.detail && <p className="text-[9px] text-muted-foreground mt-0.5">{flag.detail}</p>}
                                </div>
                              </div>
                              {showPriceUpdate && (
                                <PriceUpdateAction flag={flag} currentRow={row} relatedRowId={flag.relatedRowId!} allRows={rows} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                      {result.reviewNotes && (
                        <div className="flex items-start gap-1.5 text-[10px] bg-secondary/20 rounded px-2 py-1.5">
                          <MessageSquare className="w-3 h-3 text-muted-foreground flex-shrink-0 mt-0.5" />
                          <div>
                            <span className="text-muted-foreground">Note: </span><span className="text-foreground">{result.reviewNotes}</span>
                            {result.reviewedBy && <span className="text-muted-foreground/60 ml-1">— {result.reviewedBy}</span>}
                          </div>
                        </div>
                      )}
                      <div className="space-y-1.5 pt-1 border-t border-border/30">
                        <Textarea value={reviewNote} onChange={e => setReviewNote(e.target.value)} placeholder="Add a review note (optional)..." className="dark-input text-xs min-h-[40px] resize-none" />
                        <div className="flex gap-1.5">
                          <Button size="sm" onClick={() => handleReview(result, 'reviewed')} disabled={saving}
                            className="h-6 text-[10px] px-2 bg-green-900/40 text-green-400 border border-green-700/40 hover:bg-green-900/60">
                            <ShieldCheck className="w-3 h-3 mr-1" /> Mark Reviewed
                          </Button>
                          <Button size="sm" onClick={() => handleReview(result, 'ignored')} disabled={saving}
                            className="h-6 text-[10px] px-2 bg-gray-800/40 text-gray-400 border border-gray-700/40 hover:bg-gray-800/60">
                            <EyeOff className="w-3 h-3 mr-1" /> Ignore
                          </Button>
                          {result.reviewStatus !== 'flagged' && (
                            <Button size="sm" onClick={() => handleReview(result, 'flagged')} disabled={saving}
                              className="h-6 text-[10px] px-2 bg-amber-900/40 text-amber-400 border border-amber-700/40 hover:bg-amber-900/60">
                              <AlertTriangle className="w-3 h-3 mr-1" /> Re-flag
                            </Button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer */}
          {anomalySummary && anomalySummary.totalFlagged > 0 && (
            <div className="px-5 py-2 border-t border-border/40 flex items-center gap-2 text-[10px] text-muted-foreground flex-shrink-0">
              <BarChart3 className="w-3 h-3" />
              <span>
                {filteredResults.length} of {anomalySummary.totalFlagged} shown
                {filterType !== 'all' && ` (filtered: ${ANOMALY_TYPE_INFO[filterType].label})`}
                {filterStatus !== 'all' && ` (${filterStatus})`}
              </span>
              {selectedCount > 0 && (
                <>
                  <div className="h-3 w-px bg-border/40" />
                  <span className="text-indigo-400">{selectedCount} selected</span>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default AnomalyPanel;

